/**
 * deepspace test [suite] [--port N]
 *
 * Runs tests for a DeepSpace app. Always uses dev workers.
 *
 *   deepspace test              # smoke + api (quick check)
 *   deepspace test smoke        # smoke tests only
 *   deepspace test api          # API tests only
 *   deepspace test e2e          # all Playwright tests
 *   deepspace test unit         # vitest unit tests
 *   deepspace test all          # everything
 *   deepspace test <file>       # run specific test file
 *
 * Port is `--port` > $DEEPSPACE_PORT > 5173. The chosen port is exported as
 * DEEPSPACE_PORT to the Playwright child so the config + webServer both bind
 * to the same address. Pass a different port per app to run multiple apps
 * (and test suites) in parallel.
 */

import { defineCommand } from 'citty'
import { resolve } from 'node:path'
import { sync as spawnSync } from 'cross-spawn'
import { ensureToken } from '../auth'
import { findAppDir, findChildApps } from '../lib/app-context'
import { writeDevVars } from '../env'
import { decodeJwtPayload } from '../jwt'
import { ensureInstallReady } from '../lib/install-status'
import { ensurePlaywright } from '../lib/playwright'
import { preflightWindowsWorkerd } from '../lib/preflight'

const DEFAULT_PORT = 5173

export default defineCommand({
  meta: {
    name: 'test',
    description: 'Run tests for your DeepSpace app',
  },
  args: {
    suite: {
      type: 'positional',
      description: 'Test suite: smoke, api, e2e, unit, all (default: smoke+api)',
      required: false,
    },
    port: {
      type: 'string',
      description: `Port for vite/webServer (default ${DEFAULT_PORT}, or $DEEPSPACE_PORT)`,
      required: false,
    },
  },
  async run({ args }) {
    const suite = args.suite ?? 'default'
    const port = resolvePort(args.port)

    // Resolve the app root by walking up from cwd, matching `deepspace dev`.
    const start = resolve('.')
    const appDir = findAppDir(start)
    if (!appDir) {
      console.error(`No wrangler.toml found at or above ${start}.`)
      const children = findChildApps(start)
      if (children.length > 0) {
        console.error('Did you mean to run inside one of these app directories?')
        for (const c of children) console.error(`  cd ${c}`)
      } else {
        console.error('Run from a DeepSpace app directory (one containing wrangler.toml).')
      }
      process.exit(1)
    }

    ensureInstallReady(appDir)

    // Always write .dev.vars pointing to dev workers. A logged-in user is
    // required so writeDevVars can mint APP_OWNER_JWT via the auth-worker.
    let token: string
    let ownerId: string
    try {
      token = await ensureToken()
      const payload = decodeJwtPayload<{ sub: string }>(token)
      ownerId = payload.sub
    } catch (err) {
      console.error('`deepspace test` requires you to be logged in. Run `deepspace login` first.')
      process.exit(1)
    }

    await writeDevVars(appDir, 'dev', ownerId, token)

    if (suite !== 'unit') {
      preflightWindowsWorkerd(appDir)
      ensurePlaywright(appDir)
    }

    let exitCode = 0

    switch (suite) {
      case 'smoke':
        exitCode = runPlaywright(appDir, ['tests/smoke.spec.ts'], port)
        break
      case 'api':
        exitCode = runPlaywright(appDir, ['tests/api.spec.ts'], port)
        break
      case 'e2e':
        exitCode = runPlaywright(appDir, [], port)
        break
      case 'unit':
        exitCode = runVitest(appDir)
        break
      case 'all':
        exitCode = runVitest(appDir)
        if (exitCode === 0) exitCode = runPlaywright(appDir, [], port)
        break
      case 'default':
        exitCode = runPlaywright(appDir, ['tests/smoke.spec.ts', 'tests/api.spec.ts'], port)
        break
      default:
        if (suite.endsWith('.spec.ts')) {
          exitCode = runPlaywright(appDir, [suite], port)
        } else {
          console.error(`Unknown test suite: ${suite}`)
          console.error('Available: smoke, api, e2e, unit, all')
          process.exit(1)
        }
    }

    process.exit(exitCode)
  },
})

function runPlaywright(appDir: string, testFiles: string[], port: number): number {
  const result = spawnSync('npx', [
    'playwright', 'test',
    '--config', 'tests/playwright.config.ts',
    ...testFiles,
  ], {
    cwd: appDir,
    stdio: 'inherit',
    env: { ...process.env, DEEPSPACE_PORT: String(port) },
  })
  return result.status ?? 1
}

function runVitest(appDir: string): number {
  const result = spawnSync('npx', ['vitest', 'run', '--passWithNoTests'], {
    cwd: appDir, stdio: 'inherit',
  })
  return result.status ?? 1
}

function resolvePort(arg?: string): number {
  const raw = arg ?? process.env.DEEPSPACE_PORT
  if (!raw) return DEFAULT_PORT
  const n = Number(raw)
  if (!Number.isInteger(n) || n < 1 || n > 65535) {
    console.error(`Invalid port: ${raw}. Must be an integer between 1 and 65535.`)
    process.exit(1)
  }
  return n
}
